// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ToggleFootprintKindAction } from '../mapCommonActions';
import { kindLineColor, KIND_DASHES } from './footprintGeoJson';
import stylesDef from './styles';

// Same section names as the Data Sets panel, so the surfaces speak one
// language about file kinds.
export const KIND_LABELS: Record<FileSelectionKind, string> = {
  tif: 'Imagery', las: 'Point Clouds', pano: 'Panoramas', poleImage: 'Pole Images',
};

interface ComponentProps {
  kinds: FileSelectionKind[];
  // 'card' keeps the floating card's shipped look untouched (label left,
  // line swatch pinned right) - the customer restore path. 'pane' is the
  // admin pane look: checkbox, box swatch, label in one left cluster.
  variant: 'card' | 'pane';
}

// The per-kind filter rows shared by the on-map card and the admin pane
// control: checkbox = participation (an unchecked kind leaves the MAP
// entirely while its sidebar rows keep working, D77b/D78), swatch = the
// kind's outline style.
export const FootprintKindRows: React.FC<ComponentProps> = ({ kinds, variant }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const hiddenKinds = useAppSelector((state) => state.map.common.hiddenFootprintKinds);

  const checkboxFor = (kind: FileSelectionKind) => (
    <Checkbox
      size="small"
      sx={styles.kindCheckbox}
      checked={!hiddenKinds.includes(kind)}
      onChange={() => dispatch(ToggleFootprintKindAction(kind))}
    />
  );

  if (variant === 'pane') {
    return (
      <Box sx={styles.kindRows}>
        {kinds.map((kind) => (
          <Box key={kind} sx={styles.paneKindRow}>
            <FormControlLabel
              sx={styles.kindControl}
              label={(
                <Box sx={styles.kindLabelWrap}>
                  {/* A small BOX swatch - extents are boxes, so the sample
                      is one too, in the kind's outline color and dash. */}
                  <svg width="18" height="12" aria-hidden="true">
                    <rect
                      x="1.5"
                      y="1.5"
                      width="15"
                      height="9"
                      fill="none"
                      stroke={kindLineColor(kind, theme.palette.secondary.main)}
                      strokeWidth="2"
                      strokeDasharray={KIND_DASHES[kind]?.map((d) => d * 2).join(' ')}
                    />
                  </svg>
                  <Typography sx={styles.switchLabel}>{KIND_LABELS[kind]}</Typography>
                </Box>
              )}
              control={checkboxFor(kind)}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={styles.kindRows}>
      {kinds.map((kind) => (
        <Box key={kind} sx={styles.kindRow}>
          <FormControlLabel
            sx={styles.kindControl}
            label={<Typography sx={styles.switchLabel}>{KIND_LABELS[kind]}</Typography>}
            control={checkboxFor(kind)}
          />
          {/* Dashes scaled up with the heavier swatch stroke so the sample
              reads like the map line, not a finer pattern. */}
          <svg width="26" height="4" aria-hidden="true">
            <line
              x1="0"
              y1="2"
              x2="26"
              y2="2"
              stroke={kindLineColor(kind, theme.palette.secondary.main)}
              strokeWidth="3"
              strokeDasharray={KIND_DASHES[kind]?.map((d) => d * 2).join(' ')}
            />
          </svg>
        </Box>
      ))}
    </Box>
  );
};

export default FootprintKindRows;
